import { useState, useRef, useEffect, useMemo } from 'react'
import { useDms } from '../queries/useDms.js'
import { useUIStore } from '../store/uiStore.js'
import { colorFor } from '../data/userColors.js'
import { timeAgo } from '../data/metaKeys.js'
import { firstUrl, parseSegments } from '../data/linkDetection.js'
import EmojiPickerButton from './EmojiPickerButton.jsx'
import LinkPreviewCard from './LinkPreviewCard.jsx'

/**
 * DmPanel — Slack-style docked chat popup at the bottom-right of the
 * screen. Rendered only when `openDm` is set in the UI store.
 *
 * Features:
 *   - Header with the other participant's avatar, name, and close button
 *   - Scrollable message list, messages grouped by author
 *   - Bubble style: your messages right-aligned, theirs left
 *   - Timestamps under bubbles with timeAgo()
 *   - Input at bottom with Cmd+Enter to send
 *   - Auto-scroll to bottom on new messages
 *   - Marks the thread as read whenever it's open
 */
export default function DmPanel() {
  const openDm = useUIStore((s) => s.openDm)
  const closeDm = useUIStore((s) => s.closeDm)
  const currentUser = useUIStore((s) => s.currentUser)
  const [text, setText] = useState('')
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)

  const otherParticipants = openDm?.participants || []
  const { messages, sendMessage, markAsRead } = useDms(otherParticipants)
  const isGroup = otherParticipants.length > 1
  // For 1-on-1s we show a single avatar in the "primary" slot. For groups
  // we stack the first two and use a neutral color for the header.
  const primary = otherParticipants[0]
  const primaryColor = colorFor(primary)
  const headerTitle = isGroup
    ? otherParticipants.slice(0, 3).join(', ') +
      (otherParticipants.length > 3 ? ` +${otherParticipants.length - 3}` : '')
    : primary

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length])

  // Close on Escape
  useEffect(() => {
    if (!openDm) return
    const onKey = (e) => {
      if (e.key === 'Escape') closeDm()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [openDm, closeDm])

  // Mark as read whenever the panel is open and new messages arrive.
  // Intentionally omit `markAsRead` from the deps — it's recreated every
  // render, and including it creates an infinite render loop.
  useEffect(() => {
    if (!openDm) return
    markAsRead()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDm, messages.length])

  const handleSend = () => {
    if (!text.trim()) return
    sendMessage(text)
    setText('')
  }

  // Group consecutive messages from the same author
  const groups = useMemo(() => groupMessages(messages), [messages])

  if (!openDm) return null

  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: 360,
        maxHeight: 500,
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 12,
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 920,
        overflow: 'hidden',
        animation: 'slideInUp 0.2s ease-out',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: '#fafafa',
        }}
      >
        {isGroup ? (
          <GroupAvatarStack participants={otherParticipants} />
        ) : (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: primaryColor,
              color: '#fff',
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {primary?.[0]}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={isGroup ? otherParticipants.join(', ') : undefined}
          >
            {headerTitle}
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)' }}>
            {isGroup ? `Group of ${otherParticipants.length + 1}` : 'Direct message'}
          </div>
        </div>
        <button
          onClick={closeDm}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 18,
            color: 'var(--muted)',
            cursor: 'pointer',
            padding: 4,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 12,
          background: '#fff',
          minHeight: 200,
          maxHeight: 340,
        }}
      >
        {groups.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: 'var(--muted-light)',
              textAlign: 'center',
              fontStyle: 'italic',
              padding: '20px 10px',
            }}
          >
            No messages yet. Say hi to {isGroup ? 'the group' : primary}.
          </div>
        ) : (
          groups.map((g, i) => (
            <MessageGroup
              key={i}
              group={g}
              isMe={g.author === currentUser}
              color={colorFor(g.author)}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <div
        style={{
          padding: 10,
          borderTop: '1px solid var(--border)',
          background: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <textarea
          ref={textareaRef}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter adds a newline (standard chat UX)
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder={`Message ${isGroup ? 'the group' : primary}… (Shift+Enter for newline)`}
          style={{
            width: '100%',
            minHeight: 38,
            maxHeight: 120,
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 12,
            fontFamily: 'inherit',
            resize: 'none',
            outline: 'none',
            lineHeight: 1.4,
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EmojiPickerButton
            textareaRef={textareaRef}
            value={text}
            setValue={setText}
            direction="above"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim()}
            aria-label="Send"
            style={{
              marginLeft: 'auto',
              border: 'none',
              padding: '6px 14px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              background: text.trim() ? 'var(--accent)' : '#e5e7eb',
              color: text.trim() ? '#fff' : '#9ca3af',
              cursor: text.trim() ? 'pointer' : 'not-allowed',
              whiteSpace: 'nowrap',
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─── Components ────────────────────────────────────────────────── */

// Stack the first 2–3 participants' avatars in an overlapping circle.
// If there are more, a "+N" badge is rendered after.
function GroupAvatarStack({ participants }) {
  const visible = participants.slice(0, 3)
  const extras = participants.length - visible.length
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 28,
        height: 28,
      }}
    >
      {visible.map((name, i) => (
        <span
          key={name}
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: colorFor(name),
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            lineHeight: '22px',
            textAlign: 'center',
            border: '2px solid #fff',
            marginLeft: i === 0 ? 0 : -8,
            zIndex: visible.length - i,
            boxSizing: 'content-box',
          }}
        >
          {name[0]}
        </span>
      ))}
      {extras > 0 && (
        <span
          style={{
            marginLeft: -6,
            fontSize: 9,
            fontWeight: 700,
            color: 'var(--muted)',
            background: '#f3f4f6',
            border: '2px solid #fff',
            borderRadius: '50%',
            width: 22,
            height: 22,
            lineHeight: '22px',
            textAlign: 'center',
          }}
        >
          +{extras}
        </span>
      )}
    </div>
  )
}

/* ─── Helpers ───────────────────────────────────────────────────── */

// Render message text with bare URLs turned into clickable links. In "my"
// bubbles we use white-on-color so the default accent color would disappear —
// we explicitly style links in those bubbles to stay visible.
function renderMessageText(text, isMe) {
  const segments = parseSegments(text)
  return segments.map((seg, i) =>
    seg.type === 'url' ? (
      <a
        key={i}
        href={seg.value}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          color: isMe ? '#fff' : 'var(--accent)',
          textDecoration: 'underline',
          wordBreak: 'break-all',
        }}
      >
        {seg.value}
      </a>
    ) : (
      <span key={i}>{seg.value}</span>
    )
  )
}

function groupMessages(messages) {
  const groups = []
  for (const msg of messages) {
    const last = groups[groups.length - 1]
    if (
      last &&
      last.author === msg.author &&
      // Same author and within 2 minutes = grouped
      new Date(msg.at) - new Date(last.messages[last.messages.length - 1].at) <
        2 * 60 * 1000
    ) {
      last.messages.push(msg)
    } else {
      groups.push({ author: msg.author, messages: [msg] })
    }
  }
  return groups
}

function MessageGroup({ group, isMe, color }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isMe ? 'row-reverse' : 'row',
        gap: 8,
        marginBottom: 10,
        alignItems: 'flex-end',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: color,
          color: '#fff',
          fontSize: 11,
          fontWeight: 800,
          flexShrink: 0,
        }}
      >
        {group.author[0]}
      </span>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMe ? 'flex-end' : 'flex-start',
          maxWidth: 240,
          gap: 2,
        }}
      >
        {group.messages.map((msg, i) => {
          const url = firstUrl(msg.text)
          return (
            <div
              key={msg.id || i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start',
                gap: 2,
                maxWidth: '100%',
              }}
            >
              <div
                style={{
                  background: isMe ? color : '#f3f4f6',
                  color: isMe ? '#fff' : 'var(--text-secondary)',
                  padding: '7px 10px',
                  borderRadius: 12,
                  fontSize: 12,
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {renderMessageText(msg.text, isMe)}
              </div>
              {url && <LinkPreviewCard url={url} compact />}
            </div>
          )
        })}
        <div style={{ fontSize: 9, color: 'var(--muted-light)', padding: '0 4px' }}>
          {group.author} · {timeAgo(new Date(group.messages[group.messages.length - 1].at).getTime())}
        </div>
      </div>
    </div>
  )
}
